import React, { useCallback, useMemo, useState } from "react"
import { Box, BoxProps, FormLabel, InputGroup, Textarea, TextareaProps } from "@chakra-ui/react"

import { useDom } from "@app/hooks/useDom"
import { FormErrors } from "./FormErrors"

import type { DisplayableErrorProps } from "@root/types/global"

type Props = TextareaProps & {
  autoFocus?: boolean
  background?: string
  containerProps?: BoxProps
  errors?: Array<DisplayableErrorProps | string>
  id?: string
  isDisabled?: boolean
  name: string
  placeholder?: string
  reference?: React.Ref<HTMLTextAreaElement>
  value?: string
  variant?: string
  errorAlignment?: BoxProps["justifyContent"]
  errorIcon?: boolean
  labelColor?: string
  borderRadius?: string
}

export const FormTextarea: React.FC<Props> = ({
  autoFocus: rawAutoFocus = false,
  background = "white",
  containerProps,
  errors = [],
  id,
  isDisabled,
  name,
  onBlur,
  onFocus,
  placeholder = null,
  reference = null,
  value,
  variant,
  labelColor = "grey.mid",
  errorAlignment = "center",
  errorIcon = true,
  borderRadius = "md",
  ...props
}) => {
  const { navigator } = useDom()

  const autoFocus = useMemo(() => (rawAutoFocus && !navigator.iOS ? true : false), [navigator.iOS, rawAutoFocus])

  const [focus, setFocus] = useState(autoFocus)

  const handleBlur = useCallback(
    event => {
      setFocus(false)

      if (onBlur) onBlur(event)
    },
    [onBlur, setFocus]
  )

  const handleFocus = useCallback(
    event => {
      setFocus(true)
      if (onFocus) onFocus(event)
    },
    [onFocus, setFocus]
  )

  return (
    <Box {...containerProps}>
      <InputGroup
        alignItems="start"
        color="grey.inactive"
        pos="relative"
        transition="color 0.2s ease"
        _focusWithin={{ color: "grey.text" }}
      >
        <Textarea
          css={{
            "&::-webkit-resizer": {
              borderWidth: "20px",
              borderStyle: "solid",
              borderColor: "transparent #EDECEB #EDECEB transparent",
            },
            "&:hover::-webkit-resizer": {
              borderColor: "transparent #DADADA #DADADA transparent",
            },
          }}
          id={id || name}
          isDisabled={isDisabled}
          isInvalid={errors?.length > 0}
          name={name}
          onBlur={handleBlur}
          onFocus={handleFocus}
          ref={reference ? reference : null}
          value={value}
          variant={variant}
          rounded={borderRadius}
          {...props}
        />
        {placeholder && (
          <FormLabel
            bg={focus || value ? background : "transparent"}
            htmlFor={id || name}
            mt={4}
            size={focus || value ? "active" : "base"}
            variant={isDisabled ? "disabled" : variant}
            color={labelColor}
          >
            {placeholder}
          </FormLabel>
        )}
      </InputGroup>
      <FormErrors errors={errors} errorIcon={errorIcon} justifyContent={errorAlignment} mt={2} />
    </Box>
  )
}
